/*
  Aloha Amino — Homepage marketing campaigns
  ────────────────────────────────────────────────────────────────────────────
  One place to define every "campaign homepage" you want to A/B test. Each
  campaign is a short Hawaiian codeword you append to the homepage link:

      https://alohaaminos.com/?c=mahalo

  When a visitor lands on that link we:
    1. swap the hero copy to that campaign's angle (so each marketing effort
       gets a genuinely different homepage), and
    2. tag every analytics event for the rest of their visit with the campaign
       name — so the admin Analytics page can show you which campaign is
       actually converting traffic into engaged shoppers.

  The codeword is intentionally SHORT so it's easy to drop into an ad, a QR
  code, a flyer or an influencer link. `?c=mahalo` is just shorthand for the
  standard `?utm_campaign=mahalo`; both work and both are tracked the same way.

  COMPLIANCE: research-only product. Hero copy must NOT promise therapeutic /
  human-use effects. Keep the research-only framing intact.
*/

// The hero copy shown when no campaign is set (the plain homepage).
export const DEFAULT_HERO = {
  eyebrow: 'Research peptides for verified labs',
  headline: ['Research-grade peptides,', 'tested to the milligram.'],
  sub: 'Every Aloha Amino vial is identity- and purity-tested before it ships, with a third-party certificate of analysis behind every batch.',
};

// ──────────────────────────────────────────────────────────────────────────
// The 10 campaigns. `slug` is the short word you put in the link (?c=slug).
// `name` is the human label shown in the admin report. The hero fields swap
// the homepage headline so each marketing strategy reads differently.
// ──────────────────────────────────────────────────────────────────────────
export const CAMPAIGNS = [
  {
    slug: 'aloha',
    name: 'Aloha — warm welcome',
    eyebrow: 'Aloha · verified labs only',
    headline: ['Aloha. Research-grade peptides,', 'tested to the milligram.'],
    sub: 'Every Aloha Amino vial is identity- and purity-tested before it ships, with a third-party certificate of analysis behind every batch.',
  },
  {
    slug: 'mahalo',
    name: 'Mahalo — gratitude / loyalty',
    eyebrow: 'Mahalo · thank-you pricing',
    headline: ['Mahalo for researching with us —', 'verified peptides, every order.'],
    sub: 'A thank-you to the labs that trust us: third-party-tested peptides, a published COA on every lot, and pricing that rewards repeat research.',
  },
  {
    slug: 'kai',
    name: 'Kai — purity / clean',
    eyebrow: 'Kai · pure to the source',
    headline: ['Peptides as clean as', 'the Pacific itself.'],
    sub: 'Identity- and purity-tested to ≥ 99% by HPLC, with a third-party certificate of analysis behind every single batch.',
  },
  {
    slug: 'pono',
    name: 'Pono — integrity / quality',
    eyebrow: 'Pono · done right',
    headline: ['Done pono —', 'tested, traced, certified.'],
    sub: 'Every lot is identity-verified, purity-tested and lot-traceable, with the certificate of analysis published on request. No shortcuts.',
  },
  {
    slug: 'ohana',
    name: 'Ohana — membership / community',
    eyebrow: 'Ohana · member pricing',
    headline: ['Join the research ohana —', 'member pricing on every vial.'],
    sub: 'Members unlock lower per-vial pricing and free shipping on the same third-party-tested, COA-backed peptides. One lab family.',
  },
  {
    slug: 'nui',
    name: 'Nui — bulk / value',
    eyebrow: 'Nui · more per order',
    headline: ['Stock the whole lab —', 'bulk peptides, less per mg.'],
    sub: 'Order at volume and bring your per-milligram cost down, without giving up the COA, the purity testing or the lot traceability.',
  },
  {
    slug: 'koa',
    name: 'Koa — strength / serious research',
    eyebrow: 'Koa · built for serious labs',
    headline: ['Built for serious', 'research, start to finish.'],
    sub: 'Reference-grade peptides for demanding protocols — identity-tested, ≥ 99% purity, and lot-traceable from vial back to batch.',
  },
  {
    slug: 'lani',
    name: 'Lani — premium tier',
    eyebrow: 'Lani · a higher standard',
    headline: ['Peptides held to a', 'higher standard.'],
    sub: 'Premium reference material: HPLC-verified purity, third-party COAs and meticulous lot control on every order we ship.',
  },
  {
    slug: 'honu',
    name: 'Honu — longevity / reliability',
    eyebrow: 'Honu · steady and reliable',
    headline: ['Research supply that', 'goes the distance.'],
    sub: 'Consistent batch-to-batch quality your protocols can rely on — purity-tested, lot-traceable, and backed by a published COA.',
  },
  {
    slug: 'wai',
    name: 'Wai — fresh / made-to-order',
    eyebrow: 'Wai · fresh to the source',
    headline: ['Fresh-made peptides,', 'pure to the source.'],
    sub: 'Made and tested in-house in the USA, then shipped fresh — with identity, purity and a certificate of analysis on every lot.',
  },
];

// Lowercased slug → campaign object, for fast lookups.
const BY_SLUG = CAMPAIGNS.reduce((m, c) => { m[c.slug.toLowerCase()] = c; return m; }, {});

const STORAGE_KEY = 'tt_campaign';

/**
 * Resolve the campaign slug for the current visit.
 *  1. ?c=<slug> on the URL          (short link — preferred)
 *  2. ?utm_campaign=<slug>          (standard UTM link)
 *  3. previously-stored slug        (sticky for the rest of the session)
 * Returns '' when no campaign applies.
 */
export function getCampaignSlug() {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = (params.get('c') || params.get('utm_campaign') || '').trim().toLowerCase();
    if (fromUrl) {
      try { sessionStorage.setItem(STORAGE_KEY, fromUrl); } catch {}
      return fromUrl;
    }
    try { return (sessionStorage.getItem(STORAGE_KEY) || '').toLowerCase(); } catch { return ''; }
  } catch { return ''; }
}

/** The active campaign object (with hero copy), or null for the plain homepage. */
export function getActiveCampaign() {
  return BY_SLUG[getCampaignSlug()] || null;
}

/** The hero block to render — campaign override if any, else the default. */
export function getActiveHero() {
  return getActiveCampaign() || DEFAULT_HERO;
}
